import { useCallback, useState } from "react"

import { Button } from "antd"
import axios from "axios"
import toast from "react-hot-toast"
import { Link, NavLink } from "react-router-dom"
import { BeatLoader } from "react-spinners"

import { config } from "../../config"

import logo from "../../assets/go-logo.svg"

import styled, { css } from "styled-components"

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/
const SENDGRID_URL = "https://api.sendgrid.com/v3/marketing/contacts"

const FOOTER_LINKS = {
   useful: [
      { label: "Partner with us", to: "/partner" },
      { label: "Contact", to: "/contact" },
      { label: "FAQs", to: "/faqs" },
   ],
   company: [
      { label: "Privacy Policy", to: "/policy" },
      { label: "Terms of Use", to: "/terms" },
   ],
}

const FooterLinkSection = ({ title, links, theme }) => (
   <Item $theme={theme}>
      <h3>{title}</h3>
      <List $theme={theme}>
         {links.map(({ label, to }) => (
            <li key={label}>
               <Link to={to}>{label}</Link>
            </li>
         ))}
      </List>
   </Item>
)

const FooterBottom = ({ width, theme = "dark" }) => {
   const [email, setEmail] = useState("")
   const [loading, setLoading] = useState(false)

   const isValidEmail = EMAIL_REGEX.test(email)

   const addContact = useCallback(async () => {
      if (!email || !isValidEmail) {
         toast.error("Please enter a valid email")
         return
      }

      setLoading(true)
      try {
         await axios.put(SENDGRID_URL, {
            contacts: [{ email }],
            headers: {
               Authorization: `Bearer ${config.REACT_APP_SENDGRID_API_KEY}`,
            },
         })
         toast.success("Subscribed successfully")
         setEmail("")
      } catch {
         toast.error("An error occurred")
      } finally {
         setLoading(false)
      }
   }, [email, isValidEmail])

   return (
      <Inner width={width} $theme={theme}>
         <Item $theme={theme} className="description-item">
            <NavLink to="/">
               <img src={logo} alt="logo" className="pb-4 max-h-12" />
            </NavLink>
            <p>
               We are committed to providing top-notch haulage, land freight transportation, track logistics and income
               for companies
            </p>
         </Item>

         <FooterLinkSection title="Useful links" links={FOOTER_LINKS.useful} theme={theme} />
         <FooterLinkSection title="Company" links={FOOTER_LINKS.company} theme={theme} />

         <Item $theme={theme}>
            <h3>Newsletter</h3>
            <NewsletterContainer $theme={theme}>
               <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" />
               <Button type="primary" onClick={addContact}>
                  {loading ? <BeatLoader color="#ffffff" loading={loading} size={10} /> : "Subscribe"}
               </Button>
            </NewsletterContainer>
         </Item>
      </Inner>
   )
}

export default FooterBottom

const lightStyles = css`
   background: #fff;
`

const Inner = styled.div`
   width: ${({ width }) => width};
   margin: 0 auto;
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
   gap: 40px;
   padding: 60px 0;

   ${({ $theme }) => $theme === "light" && lightStyles}

   .description-item {
      grid-column: 1;

      p {
         color: ${({ $theme }) => ($theme === "light" ? "#4b5563" : "#9ca3af")};
         line-height: 1.6;
         margin-top: 15px;
      }
   }

   @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 30px;
      padding: 50px 0;
   }

   @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 25px;
      padding: 40px 0;

      .description-item {
         grid-column: 1;
      }
   }

   @media (max-width: 480px) {
      gap: 20px;
      padding: 30px 0;
   }
`

const Item = styled.div`
   h3 {
      color: ${({ $theme }) => ($theme === "light" ? "#101828" : "white")};
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 20px;
   }

   input {
      height: 40px;
      padding: 0 15px;
      background: ${({ $theme }) => ($theme === "light" ? "#f3f4f6" : "white")};
      border-radius: 5px;
      border: ${({ $theme }) => ($theme === "light" ? "1px solid #d1d5db" : "none")};
      margin-right: 10px;
      flex: 1;
      font-size: 14px;
      color: #1f2937;

      &::placeholder {
         color: #999;
      }
   }

   button {
      height: 40px;
      padding: 0 25px;
      font-weight: 600;
      background: #f97316 !important;
      border: none !important;
      white-space: nowrap;

      &:hover {
         background: #ea580c !important;
      }
   }

   @media (max-width: 768px) {
      h3 {
         font-size: 15px;
         margin-bottom: 16px;
      }

      input {
         height: 38px;
         padding: 0 12px;
         font-size: 13px;
      }

      button {
         height: 38px;
         padding: 0 20px;
         font-size: 12px;
      }
   }

   @media (max-width: 480px) {
      h3 {
         font-size: 14px;
         margin-bottom: 12px;
      }

      input {
         height: 36px;
         padding: 0 12px;
         font-size: 12px;
      }

      button {
         height: 36px;
         padding: 0 16px;
         font-size: 11px;
      }
   }
`

const NewsletterContainer = styled.div`
   display: flex;
   gap: 2px;
`

const List = styled.ul`
   list-style: none;
   padding: 0;
   margin: 0;

   li {
      margin-bottom: 12px;
   }

   a {
      color: ${({ $theme }) => ($theme === "light" ? "#4b5563" : "#d0d5dd")};
      text-decoration: none;
      transition: color 0.3s ease;
      font-size: 14px;

      &:hover {
         color: #f97316;
      }
   }

   @media (max-width: 768px) {
      li {
         margin-bottom: 10px;
      }

      a {
         font-size: 13px;
      }
   }

   @media (max-width: 480px) {
      li {
         margin-bottom: 8px;
      }

      a {
         font-size: 12px;
      }
   }
`
